import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { apiGet } from "../../services/api";
import PaymentPanel from "./PaymentPanel";
import Button from "../common/Button";

interface Patient {
  patient_id: string | number;
  name?: string;
}

interface ClinicalBillRecord {
  id: number;
  created_at?: string;
  amount?: number;
  paid?: number;
  balance?: number;
  status?: string;
  treatments?: { name: string; qty: number; amount: number }[];
}

interface PaymentRecord {
  id: number;
  bill_id: number;
  amount: number;
  date: string;
  mode?: string;
}

interface ManagerPatientHistoryProps {
  patient: Patient | null;
}

export default function ManagerPatientHistory({ patient }: ManagerPatientHistoryProps) {
  const [records, setRecords] = useState<{
    clinical_bills: ClinicalBillRecord[];
    payments?: PaymentRecord[];
  }>({
    clinical_bills: [],
    payments: []
  });
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [payingBillId, setPayingBillId] = useState<number | null>(null);
  const [expandedBills, setExpandedBills] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (!patient?.patient_id) return;

    setLoading(true);
    apiGet(`/api/manager/patient-records/${encodeURIComponent(String(patient.patient_id))}`)
      .then((value: unknown) => {
        const data = value as { clinical_bills?: ClinicalBillRecord[]; payments?: PaymentRecord[] };
        setRecords({
          clinical_bills: data.clinical_bills || [],
          payments: data.payments || []
        });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [patient, reloadKey]);

  const totalBilled = records.clinical_bills.reduce((sum, b) => sum + (b.amount || 0), 0);
  const totalPaid = records.clinical_bills.reduce((sum, b) => sum + (b.paid || 0), 0);
  const outstanding = Math.max(totalBilled - totalPaid, 0);

  if (!patient) return null;

  const formatDate = (raw: unknown) => {
    const s = String(raw ?? "");
    if (!s) return "—";
    return new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      
      {/* Financial Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
        <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'var(--mod-shadow-sm)' }}>
          <div style={{ fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 600, marginBottom: '4px' }}>Total Billed</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0f172a' }}>₹{totalBilled.toLocaleString()}</div>
        </div>
        <div style={{ background: '#f0fdf4', padding: '16px', borderRadius: '12px', border: '1px solid #bbf7d0', boxShadow: 'var(--mod-shadow-sm)' }}>
          <div style={{ fontSize: '0.85rem', color: '#166534', textTransform: 'uppercase', fontWeight: 600, marginBottom: '4px' }}>Total Paid</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#15803d' }}>₹{totalPaid.toLocaleString()}</div>
        </div>
        <div style={{ background: '#fef2f2', padding: '16px', borderRadius: '12px', border: '1px solid #fecaca', boxShadow: 'var(--mod-shadow-sm)' }}>
          <div style={{ fontSize: '0.85rem', color: '#991b1b', textTransform: 'uppercase', fontWeight: 600, marginBottom: '4px' }}>Outstanding Due</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#dc2626' }}>₹{outstanding.toLocaleString()}</div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#0f172a' }}>📜 Billing History</h3>
        <Button variant="outline" size="sm" onClick={() => setReloadKey(k => k + 1)} isLoading={loading}>Refresh</Button>
      </div>

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Loading complete history...</div>
      ) : records.clinical_bills.length === 0 ? (
        <div className="mod-empty-state" style={{ height: '200px', border: '1px dashed #cbd5e1' }}>
          <p style={{ color: '#64748b' }}>No clinical bills found for this patient.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {records.clinical_bills.map((b) => {
            const due = Number(b.balance ?? 0);
            const payments = (records.payments || []).filter((p) => Number(p.bill_id) === Number(b.id));
            const isExpanded = !!expandedBills[b.id];

            return (
              <div key={b.id} style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'var(--mod-shadow-sm)', overflow: 'hidden' }}>
                
                {/* Bill Header / Summary */}
                <div 
                  style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', padding: '16px', cursor: 'pointer', background: isExpanded ? '#f8fafc' : '#fff', transition: 'background 0.2s', alignItems: 'center' }}
                  onClick={() => setExpandedBills(prev => ({ ...prev, [b.id]: !prev[b.id] }))}
                >
                  <div style={{ flex: '1 1 200px' }}>
                    <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600, marginBottom: '4px' }}>{formatDate(b.created_at)}</div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a' }}>Bill #{b.id}</div>
                  </div>

                  <div style={{ flex: '2 1 300px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {(b.treatments && b.treatments.length > 0) ? (
                        b.treatments.map((t, i) => (
                           <span key={i} style={{ background: '#f1f5f9', color: '#475569', padding: '4px 8px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 500, border: '1px solid #e2e8f0' }}>
                             {t.name}{t.qty > 1 ? ` (x${t.qty})` : ""}
                           </span>
                        ))
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: '0.9rem' }}>No treatments listed</span>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>Amount</div>
                      <div style={{ fontWeight: 700 }}>₹{Number(b.amount ?? 0).toLocaleString("en-IN")}</div>
                    </div>
                    {due > 0 ? (
                      <div style={{ textAlign: 'right', background: '#fef2f2', padding: '4px 8px', borderRadius: '6px', border: '1px solid #fecaca' }}>
                        <div style={{ fontSize: '0.75rem', color: '#991b1b', textTransform: 'uppercase', fontWeight: 700 }}>Due</div>
                        <div style={{ fontWeight: 800, color: '#dc2626' }}>₹{due.toLocaleString("en-IN")}</div>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'right', background: '#f0fdf4', padding: '4px 8px', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
                        <div style={{ fontSize: '0.75rem', color: '#166534', textTransform: 'uppercase', fontWeight: 700 }}>Status</div>
                        <div style={{ fontWeight: 800, color: '#15803d' }}>PAID</div>
                      </div>
                    )}
                    
                    <span style={{ fontSize: '1.2rem', color: '#94a3b8', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▼</span>
                  </div>
                </div>

                {/* Expanded Details: Actions & Payment History */}
                {isExpanded && (
                  <div style={{ borderTop: '1px solid #e2e8f0', padding: '16px', background: '#f8fafc' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '20px' }}>
                      
                      {/* Payment History Table */}
                      <div style={{ flex: 1, minWidth: '300px' }}>
                         <h4 style={{ margin: '0 0 12px 0', fontSize: '0.9rem', color: '#475569', textTransform: 'uppercase' }}>Payment Ledger</h4>
                         {payments.length === 0 ? (
                            <div style={{ padding: '12px', background: '#fff', borderRadius: '8px', border: '1px dashed #cbd5e1', color: '#64748b', fontSize: '0.9rem' }}>
                              No payments recorded yet.
                            </div>
                         ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem', background: '#fff', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                              <thead style={{ background: '#f1f5f9' }}>
                                <tr>
                                  <th style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>Date</th>
                                  <th style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>Mode</th>
                                  <th style={{ padding: '8px 12px', textAlign: 'right', color: '#64748b', fontWeight: 600 }}>Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {payments.slice().sort((a, b2) => String(b2.date || "").localeCompare(String(a.date || ""))).map((p) => (
                                  <tr key={p.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '8px 12px' }}>{formatDate(p.date)}</td>
                                    <td style={{ padding: '8px 12px' }}>{p.mode || "—"}</td>
                                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#16a34a' }}>₹{Number(p.amount || 0).toLocaleString("en-IN")}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                         )}
                      </div>

                      {/* Quick Actions */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '150px' }}>
                         <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); window.open(`/print?type=bill&bill=${b.id}`, "_blank"); }}>
                           🖨️ Print Bill
                         </Button>
                         {due > 0 && (
                           <Button variant="primary" size="sm" onClick={(e) => { e.stopPropagation(); setPayingBillId(b.id); }}>
                             + Pay Balance
                           </Button>
                         )}
                      </div>

                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modern Portal Modal for Payment */}
      {payingBillId && typeof document !== "undefined" && createPortal(
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setPayingBillId(null)}>
          <div style={{ width: '500px', background: '#fff', padding: '24px', borderRadius: '16px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#0f172a' }}>Pay Balance</h3>
              <button onClick={() => setPayingBillId(null)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#64748b' }}>&times;</button>
            </div>
            <div style={{ marginBottom: '20px', padding: '16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.9rem', color: '#475569' }}>
              Recording payment for <strong>Bill #{payingBillId}</strong>
            </div>
            
            <PaymentPanel 
              billId={payingBillId} 
              onPaid={() => {
                setPayingBillId(null);
                setReloadKey(k => k + 1);
              }} 
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
